import { container } from '@web/services/container';
import { type TechlineCaseSummary } from '@packages/domain/techline/cases';
import { CreateCase, FetchCases } from '@packages/domain/techline';

import { useCallback, useState } from 'react';
import { useAbortController } from '@packages/core/http';
import { useAuthState } from '@packages/contexts/auth';
import { useRetailerProfileOptions } from './use-retailer-profiles-options';
import { useVinSearch } from './use-vin-search';

import { OPEN_CASE_STATUSES } from '@packages/domain/techline/cases';

const [$createCase, $fetchCases] = container.resolveOperations(CreateCase, FetchCases);

/**
 * Custom hook for starting a new techline case, including VIN search, retailer profile options,
 * fetching existing cases, and creating a draft case. This hook composes multiple smaller hooks
 * that manage different pieces of state and logic related to starting a new case.
 *
 */
export function useStartNewCase() {
    const { account } = useAuthState();
    const { abortSignalRef } = useAbortController();

    const vinSearch = useVinSearch();
    const retailerProfileOptions = useRetailerProfileOptions();

    const { resetVinSearch, vehicleDetails } = vinSearch;
    const { resetRetailerProfileOptions, selectedRetailerProfile } = retailerProfileOptions;

    const [existingCases, setExistingCases] = useState<TechlineCaseSummary[]>([]);
    const [isCreatingCase, setIsCreatingCase] = useState(false);
    const [isLoadingExistingCases, setIsLoadingExistingCases] = useState(false);

    /**
     * Fetch open (non-closed) cases for the selected retailer and VIN.
     */
    const fetchExistingCases = useCallback(async () => {
        setExistingCases([]);
        setIsLoadingExistingCases(true);

        if (!selectedRetailerProfile[0]?.retailerNumber) {
            setIsLoadingExistingCases(false);
            return [];
        }

        const caseResults = await $fetchCases(
            {
                retailerNumber: selectedRetailerProfile[0].retailerNumber,
                vin: vehicleDetails?.vin,
                techlineCaseStatus: OPEN_CASE_STATUSES,
            },
            abortSignalRef.current
        );

        if (!caseResults) {
            setIsLoadingExistingCases(false);
            return false;
        }

        const cases = caseResults?.results ?? [];

        setExistingCases(cases);
        setIsLoadingExistingCases(false);
        return cases;
    }, [selectedRetailerProfile, vehicleDetails, abortSignalRef]);

    /**
     * Create a draft Techline case for the selected retailer and VIN.
     * Returns the new case number on success, or undefined on failure.
     */
    const createDraftCase = useCallback(async () => {
        if (!selectedRetailerProfile[0] || !vehicleDetails || isCreatingCase) {
            return undefined;
        }

        setIsCreatingCase(true);

        const caseNumber = await $createCase(
            {
                username: account?.soaUsername || '',
                retailerNumber: selectedRetailerProfile[0].retailerNumber,
                vin: vehicleDetails.vin,
            },
            abortSignalRef.current
        );

        setIsCreatingCase(false);
        return caseNumber;
    }, [selectedRetailerProfile, vehicleDetails, isCreatingCase, account, abortSignalRef]);

    /** Reset all state to initial values. Called when the modal closes or navigates back. */
    const resetState = useCallback(() => {
        resetVinSearch();
        resetRetailerProfileOptions();
        setExistingCases([]);
        setIsCreatingCase(false);
        setIsLoadingExistingCases(false);
    }, [resetVinSearch, resetRetailerProfileOptions]);

    return {
        // Composed hooks (spread)
        ...vinSearch,
        ...retailerProfileOptions,

        createDraftCase,
        fetchExistingCases,
        resetState,
        existingCases,
        isCreatingCase,
        isLoadingExistingCases,
    };
}
